import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { V11ThemeToggle } from '@/components/v11/layout/V11ThemeToggle';
import { TokenUsagePill } from './TokenUsagePill';
import { TopbarPeriodSlotProvider } from './TopbarPeriodSlot';
import { SettingsBreadcrumb, hasBreadcrumb } from './SettingsBreadcrumb';
import { HeaderControlsBoundary } from '@/components/errors/HeaderControlsBoundary';

interface ShellTopbarProps {
  readonly pathname: string;
  /**
   * What the page injects — today the time bar with its presets and its
   * calendar. CT-0 puts the date control at this end of the topbar, which is
   * where the slot sits; the control itself still belongs to the page,
   * because the period it changes is the page's.
   */
  readonly headerControls: React.ReactNode;
  /**
   * On a phone there is no rail (AL-1972): given, the topbar starts with the
   * button that opens the sidebar in a drawer.
   */
  readonly onOpenNav?: () => void;
}

export const ShellTopbar: React.FC<ShellTopbarProps> = ({ pathname, headerControls, onOpenNav }) => {
  const { t } = useTranslation();
  // The element the time bar portals its date range into (see TopbarPeriodSlot).
  // State, not a ref, so the time bar re-renders once the element exists.
  const [periodSlot, setPeriodSlot] = useState<HTMLDivElement | null>(null);

  return (
    <TopbarPeriodSlotProvider value={periodSlot}>
      <header className="sticky top-0 z-20 border-b border-v11-border bg-v11-header-bg backdrop-blur-sm">
        {/* min-h-14: the row is 56px whether the page injects the time bar
            (32px pills) or nothing (28px theme toggle), so changing page no
            longer moves the content by a few pixels (AL-1940). A minimum, not a
            fixed height: on narrow screens the pills may wrap and need more.

            The horizontal spacing tightens on phones, where the row now sits
            beside the rail and lines up with main's px-4 (AL-1911). The brand
            link that used to live here is gone with it: the rail carries the
            wordmark at every width, so the topbar no longer has to. */}
        <div className="flex min-h-14 items-center justify-between gap-3 px-4 py-3 md:gap-6 md:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            {onOpenNav && (
              <button
                type="button"
                onClick={onOpenNav}
                aria-label={t('app.v11.shell.mobileNav.open')}
                className="-ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-v11-text-primary hover:bg-v11-surface-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-v11-accent"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
            {hasBreadcrumb(pathname) ? (
              <SettingsBreadcrumb pathname={pathname} />
            ) : (
              <>
                <div className="flex min-w-0 flex-1 items-center" data-testid="header-controls-slot">
                  {/* Page-owned, so a failure here hides the controls, not the app (AL-2004). */}
                  <HeaderControlsBoundary resetKey={pathname}>{headerControls}</HeaderControlsBoundary>
                </div>
                <TokenUsagePill />
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-4">
            <div
              ref={setPeriodSlot}
              className="flex items-center"
              data-testid="topbar-period-slot"
            />
            <V11ThemeToggle />
          </div>
        </div>
      </header>
    </TopbarPeriodSlotProvider>
  );
};
